import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  useToast,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Switch,
  Textarea,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

import { getAuthHeaders } from '../../utils/apiAuth';
export default function FaxCredentials({ config, onUpdate }) {
  const [formData, setFormData] = useState({
    access_id: config?.access_id || '',       // SMTP username
    access_pwd: config?.access_pwd || '',     // SMTP password / app password
    caller_id: config?.caller_id || '',       // eFax number (for display)
    sender_email: config?.sender_email || '', // eFax authorized sender email
    api_url: config?.api_url || '',           // SMTP host
    default_cover_page: config?.default_cover_page || '',
    default_subject: config?.default_subject || '',
    default_comments: config?.default_comments || '',
    is_active: config?.is_active ?? true,
    test_mode: config?.test_mode ?? false
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const toast = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.sender_email) {
      newErrors.sender_email = 'eFax sender email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.sender_email)) {
      newErrors.sender_email = 'Invalid email format';
    }

    if (!formData.caller_id) {
      newErrors.caller_id = 'eFax number is required';
    }

    if (!formData.access_id) {
      newErrors.access_id = 'SMTP username is required';
    }

    if (!formData.access_pwd) {
      newErrors.access_pwd = 'SMTP password is required';
    }

    if (!formData.api_url) {
      newErrors.api_url = 'SMTP host is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors above',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('fax_config')
        .upsert({
          id: config?.id || null,
          ...formData,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      onUpdate(data);
      setTestResult(null);

      toast({
        title: 'Configuration saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Error saving configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors above before testing',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch('/api/test-fax-config', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          access_id: formData.access_id,
          access_pwd: formData.access_pwd,
          caller_id: formData.caller_id,
          sender_email: formData.sender_email,
          api_url: formData.api_url
        })
      });

      const result = await response.json();

      setTestResult({
        success: response.ok,
        message: result.message || (response.ok ? 'Connection successful' : 'Connection failed'),
        details: result.details
      });

      toast({
        title: response.ok ? 'eFax connection successful' : 'eFax connection failed',
        description: !response.ok ? result.message : undefined,
        status: response.ok ? 'success' : 'error',
        duration: response.ok ? 3000 : 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Test error:', error);
      setTestResult({
        success: false,
        message: 'Connection test failed',
        details: error.message
      });

      toast({
        title: 'Test failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={2}>
          eFax Configuration
        </Text>
        <Text color="gray.600">
          Configure eFax email-to-fax settings. Faxes are sent by emailing a PDF to faxnumber@efaxsend.com via SMTP.
        </Text>
      </Box>

      {/* eFax Account */}
      <Card>
        <CardHeader>
          <HStack justify="space-between">
            <Text fontWeight="bold">eFax Account</Text>
            <HStack>
              <Badge colorScheme={formData.is_active ? 'green' : 'gray'}>
                {formData.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {formData.test_mode && (
                <Badge colorScheme="yellow">Test Mode</Badge>
              )}
            </HStack>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={errors.caller_id}>
              <FormLabel>eFax Number *</FormLabel>
              <Input
                value={formData.caller_id}
                onChange={(e) => handleInputChange('caller_id', e.target.value.replace(/\D/g, ''))}
                placeholder="9736291209"
                type="tel"
                maxLength={11}
              />
              <FormHelperText>Your eFax fax number (10 digits, no country code). Found at top of myportal.efax.com</FormHelperText>
              <FormErrorMessage>{errors.caller_id}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.sender_email}>
              <FormLabel>eFax Authorized Sender Email *</FormLabel>
              <Input
                value={formData.sender_email}
                onChange={(e) => handleInputChange('sender_email', e.target.value)}
                placeholder="your-email@gmail.com"
                type="email"
              />
              <FormHelperText>The email listed under "Send Email Addresses" in your eFax account settings</FormHelperText>
              <FormErrorMessage>{errors.sender_email}</FormErrorMessage>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* SMTP Settings */}
      <Card>
        <CardHeader>
          <Text fontWeight="bold">SMTP Settings (for sending emails)</Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md" mb={2}>
              <AlertIcon />
              <Box>
                <Text fontSize="sm" fontWeight="bold">How it works</Text>
                <Text fontSize="xs">
                  Faxes are sent by emailing a PDF attachment to [faxnumber]@efaxsend.com from your authorized email.
                  You need SMTP credentials to send emails. For Gmail, use an App Password.
                </Text>
              </Box>
            </Alert>

            <FormControl isInvalid={errors.api_url}>
              <FormLabel>SMTP Host *</FormLabel>
              <Input
                value={formData.api_url}
                onChange={(e) => handleInputChange('api_url', e.target.value)}
                placeholder="smtp.gmail.com"
              />
              <FormHelperText>For Gmail: smtp.gmail.com | For Outlook: smtp.office365.com</FormHelperText>
              <FormErrorMessage>{errors.api_url}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>SMTP Port</FormLabel>
              <Input
                value={formData.default_cover_page || '587'}
                onChange={(e) => handleInputChange('default_cover_page', e.target.value)}
                placeholder="587"
              />
              <FormHelperText>Usually 587 (TLS) or 465 (SSL)</FormHelperText>
            </FormControl>

            <FormControl isInvalid={errors.access_id}>
              <FormLabel>SMTP Username *</FormLabel>
              <Input
                value={formData.access_id}
                onChange={(e) => handleInputChange('access_id', e.target.value)}
                placeholder="your-email@gmail.com"
              />
              <FormHelperText>Usually your email address</FormHelperText>
              <FormErrorMessage>{errors.access_id}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.access_pwd}>
              <FormLabel>SMTP Password / App Password *</FormLabel>
              <HStack>
                <Input
                  value={formData.access_pwd}
                  onChange={(e) => handleInputChange('access_pwd', e.target.value)}
                  placeholder="App password (not your regular password)"
                  type={showPassword ? 'text' : 'password'}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Icon as={showPassword ? FaEyeSlash : FaEye} />
                </Button>
              </HStack>
              <FormHelperText>For Gmail: generate an App Password at myaccount.google.com/apppasswords</FormHelperText>
              <FormErrorMessage>{errors.access_pwd}</FormErrorMessage>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Default Settings */}
      <Card>
        <CardHeader>
          <Text fontWeight="bold">Default Settings</Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Default Subject</FormLabel>
              <Input
                value={formData.default_subject}
                onChange={(e) => handleInputChange('default_subject', e.target.value)}
                placeholder="Fax from AtHomeBloodDraw.com"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Default Message</FormLabel>
              <Textarea
                value={formData.default_comments}
                onChange={(e) => handleInputChange('default_comments', e.target.value)}
                placeholder="Default message body for faxes"
                rows={3}
              />
            </FormControl>

            <HStack spacing={6}>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" mr={3}>
                  <Tooltip label="Enable/disable fax functionality">
                    <Text>Active</Text>
                  </Tooltip>
                </FormLabel>
                <Switch
                  isChecked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  colorScheme="green"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" mr={3}>
                  <Tooltip label="Enable test mode (no actual faxes sent)">
                    <Text>Test Mode</Text>
                  </Tooltip>
                </FormLabel>
                <Switch
                  isChecked={formData.test_mode}
                  onChange={(e) => handleInputChange('test_mode', e.target.checked)}
                  colorScheme="yellow"
                />
              </FormControl>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Test Result */}
      {testResult && (
        <Alert status={testResult.success ? 'success' : 'error'} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">{testResult.message}</Text>
            {testResult.details && (
              <Text fontSize="sm" mt={1}>{testResult.details}</Text>
            )}
          </Box>
        </Alert>
      )}

      {/* Actions */}
      <HStack justify="flex-end" spacing={4}>
        <Button
          onClick={handleTest}
          isLoading={testing}
          loadingText="Testing..."
          leftIcon={<Icon as={FaExclamationTriangle} />}
          colorScheme="blue"
          variant="outline"
        >
          Test Connection
        </Button>

        <Button
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving..."
          leftIcon={<Icon as={FaCheck} />}
          colorScheme="green"
        >
          Save Configuration
        </Button>
      </HStack>
    </VStack>
  );
}
